/**
 * NotificationBell — centro de notificaciones "vivo y accionable" para el médico.
 *
 * En vez de un log de notificaciones, muestra lo que NECESITA su atención AHORA
 * (estado real de la cola), desde cualquier página del portal:
 *   - Pacientes ASIGNADOS a mí pendientes de aceptar (auto-asignación).
 *   - Pacientes EN ESPERA en mis salas.
 * El badge refleja la cuenta en vivo; al hacer clic en un paciente se navega a
 * su sala para atenderlo. El supervisor ve la espera de todo el sistema.
 *
 * Alcance: SOLO médico y supervisor. Las notificaciones son de teleconsulta, y
 * ni admin ni notificador atienden pacientes, así que MainLayout no monta este
 * componente para ellos (además de evitarles la suscripción a `consultas`).
 *
 * Diseño: se prefiere un indicador accionable en tiempo real (siempre exacto,
 * sin estado leído/no-leído que mantener) sobre un historial de avisos.
 */

import { useMemo, useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, Clock, AlertTriangle, UserPlus, Users, ChevronRight } from 'lucide-react';
import { where, type QueryConstraint, type Timestamp } from 'firebase/firestore';
import { useAuth } from '../../contexts/AuthContext';
import { useCollection, useDocument } from '../../hooks/useFirestore';
import type { Consulta } from '../../types';
import nb from './NotificationBell.module.css';

/** AudioContext perezoso reutilizable (se crea al primer uso). */
let audioCtx: AudioContext | null = null;

/** Sonido breve "ding" sintetizado (sin archivo de audio) para avisar que
 *  llegó un paciente. Silencioso si el navegador aún no permite audio. */
function playDing(): void {
  try {
    const Ctx = window.AudioContext
      || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (!Ctx) return;
    if (!audioCtx) audioCtx = new Ctx();
    const ctx = audioCtx;
    if (ctx.state === 'suspended') ctx.resume().catch(() => { /* sin gesto aún */ });
    const t0 = ctx.currentTime;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(880, t0);          // nota 1
    osc.frequency.setValueAtTime(1174.66, t0 + 0.13); // nota 2 (ding-dong)
    gain.gain.setValueAtTime(0.0001, t0);
    gain.gain.exponentialRampToValueAtTime(0.2, t0 + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.55);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start(t0);
    osc.stop(t0 + 0.55);
  } catch {
    /* política de autoplay u otro — ignorar silenciosamente */
  }
}

function elapsedLabel(ts: Timestamp | undefined | null): string {
  const d = ts?.toDate?.();
  if (!d || isNaN(d.getTime())) return '';
  const min = Math.floor((Date.now() - d.getTime()) / 60000);
  if (min < 1) return 'recién';
  if (min < 60) return `hace ${min} min`;
  const h = Math.floor(min / 60);
  return `hace ${h} h`;
}

interface NotificationBellProps {
  /** Clase del botón (la del topbar) para mantener consistencia visual. */
  buttonClassName?: string;
}

export function NotificationBell({ buttonClassName }: NotificationBellProps) {
  const { user, role, claims } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [pulse, setPulse] = useState(false);
  const prevTotalRef = useRef(0);
  const baselineRef = useRef(false);

  // Estado del médico para gatear el sonido: no sonar si está EN CONSULTA, y
  // respetar su preferencia `notificacionesSonido`.
  const { data: medicoDoc } = useDocument<{ estado?: string; notificacionesSonido?: boolean }>(
    'medicos', user?.uid,
  );
  const enConsulta = medicoDoc?.estado === 'en_consulta';
  const sonidoActivo = medicoDoc?.notificacionesSonido !== false;

  const salaIds: string[] = useMemo(() => {
    const ids = (claims as { salaIds?: unknown })?.salaIds;
    return Array.isArray(ids) ? (ids as string[]) : [];
  }, [claims]);
  // El supervisor ve la espera de todo el sistema (las reglas de Firestore le
  // permiten leer cualquier consulta); el médico solo sus salas. Admin y
  // notificador no montan este componente (ver MainLayout).
  const verTodas = role === 'supervisor' || salaIds.includes('*');
  const salaKey = salaIds.join(',');

  // Constraints: el médico filtra por sus salas (un solo `in`, ≤10); admin ve
  // toda la espera del sistema. Sin salas → consulta vacía.
  const constraints = useMemo<QueryConstraint[]>(() => {
    if (verTodas) return [where('estado', 'in', ['en_espera', 'asignada'])];
    if (salaIds.length > 0) return [where('salaId', 'in', salaIds.slice(0, 10))];
    // Médico sin salas (caso raro): consultar solo las propias (permitido por
    // las reglas vía medicoId) para no disparar un error de permisos.
    return [where('medicoId', '==', user?.uid ?? '__ninguno__')];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [verTodas, salaKey, user?.uid]);

  const { data: consultas, loading } = useCollection<Consulta>('consultas', constraints);

  const { asignadasMias, enEspera, total } = useMemo(() => {
    const list = (consultas ?? []).filter(
      (c) =>
        (c.estado === 'en_espera' || c.estado === 'asignada') &&
        (verTodas || salaIds.includes(c.salaId)),
    );
    const asignadas = list
      .filter((c) => c.estado === 'asignada' && c.medicoId === user?.uid)
      .sort((a, b) => (a.creadoEn?.toMillis?.() ?? 0) - (b.creadoEn?.toMillis?.() ?? 0));
    const espera = list
      .filter((c) => c.estado === 'en_espera')
      .sort((a, b) => (a.creadoEn?.toMillis?.() ?? 0) - (b.creadoEn?.toMillis?.() ?? 0));
    return { asignadasMias: asignadas, enEspera: espera, total: asignadas.length + espera.length };
  }, [consultas, verTodas, salaIds, user?.uid]);

  // Pulso + sonido cuando AUMENTA la cuenta (llegó un paciente nuevo). El sonido
  // solo si el médico NO está en consulta y tiene el sonido activado. Se fija
  // una línea base tras la primera carga para no sonar al entrar a la página.
  useEffect(() => {
    if (loading) return; // aún cargando: no comparar
    if (!baselineRef.current) {
      baselineRef.current = true;
      prevTotalRef.current = total;
      return; // primera carga = línea base, sin aviso
    }
    if (total > prevTotalRef.current) {
      setPulse(true);
      if (!enConsulta && sonidoActivo) playDing();
      const t = setTimeout(() => setPulse(false), 1800);
      prevTotalRef.current = total;
      return () => clearTimeout(t);
    }
    prevTotalRef.current = total;
  }, [loading, total, enConsulta, sonidoActivo]);

  // Todas las colas viven en /salas: la vista por sala era una segunda página
  // a medias y ya no existe. Desde aquí basta con abrir la cola.
  const irASala = useCallback(() => {
    setOpen(false);
    navigate('/salas');
  }, [navigate]);

  const renderItem = (c: Consulta, asignada: boolean) => {
    // El backend puede escribir prioridad 'alta'/'urgente' (no en el union del
    // tipo); comparar como string evita el error de narrowing y cubre runtime.
    const prio = String(c.prioridad ?? '');
    const altaPrioridad = prio === 'alta' || prio === 'urgente';
    return (
      <button
        key={c.id}
        type="button"
        className={nb.item}
        onClick={() => irASala()}
        title="Ir a la sala para atender"
      >
        <span className={`${nb.dot} ${asignada ? nb.dotAsignada : nb.dotEspera} ${altaPrioridad ? nb.dotPrioridad : ''}`} />
        <span className={nb.itemBody}>
          <span className={nb.itemName}>{c.pacienteNombre || 'Paciente'}</span>
          <span className={nb.itemMeta}>
            <span>{c.salaNombre}</span>
            {c.creadoEn && <span><Clock size={10} aria-hidden="true" /> {elapsedLabel(c.creadoEn)}</span>}
            {altaPrioridad && (
              <span className={nb.prio}><AlertTriangle size={10} aria-hidden="true" /> {prio}</span>
            )}
          </span>
        </span>
        <ChevronRight size={14} className={nb.itemChev} aria-hidden="true" />
      </button>
    );
  };

  return (
    <div className={nb.wrap}>
      <button
        type="button"
        className={buttonClassName}
        onClick={() => setOpen((v) => !v)}
        aria-label={`Notificaciones${total > 0 ? ` (${total})` : ''}`}
        aria-expanded={open}
      >
        <Bell size={16} />
        {total > 0 && (
          <span className={`${nb.badge} ${pulse ? nb.badgePulse : ''}`}>
            {total > 9 ? '9+' : total}
          </span>
        )}
      </button>

      {open && (
        <>
          <div className={nb.overlay} onClick={() => setOpen(false)} />
          <div className={nb.panel} role="menu">
            <div className={nb.head}>
              <span className={nb.headTitle}>Notificaciones</span>
              {total > 0 && <span className={nb.headCount}>{total}</span>}
            </div>

            <div className={nb.body}>
              {total === 0 ? (
                <div className={nb.empty}>
                  <Bell size={22} className={nb.emptyIcon} aria-hidden="true" />
                  <p>No hay pacientes esperando ahora.</p>
                  <span className={nb.emptyHint}>Te avisaremos cuando llegue uno.</span>
                </div>
              ) : (
                <>
                  {asignadasMias.length > 0 && (
                    <div className={nb.section}>
                      <div className={nb.sectionHead}>
                        <UserPlus size={12} aria-hidden="true" /> Asignados a ti ({asignadasMias.length})
                      </div>
                      {asignadasMias.map((c) => renderItem(c, true))}
                    </div>
                  )}
                  {enEspera.length > 0 && (
                    <div className={nb.section}>
                      <div className={nb.sectionHead}>
                        <Users size={12} aria-hidden="true" /> {verTodas ? 'En espera (todas las salas)' : 'En espera en tus salas'} ({enEspera.length})
                      </div>
                      {enEspera.slice(0, 12).map((c) => renderItem(c, false))}
                      {enEspera.length > 12 && (
                        <div className={nb.more}>y {enEspera.length - 12} más…</div>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>

            <button
              type="button"
              className={nb.footer}
              onClick={() => { setOpen(false); navigate('/salas'); }}
            >
              Ir a mis salas <ChevronRight size={13} aria-hidden="true" />
            </button>
          </div>
        </>
      )}
    </div>
  );
}
